import React, { useState, useEffect, useCallback } from 'react';
import {useParams, useNavigate, Routes, Route, useLocation} from 'react-router-dom';
import { getBoardById, getCardById, updateCard, deleteCard, Card as ApiCard, Board as ApiBoard } from '../api';
import BoardDetail, { ListWithCards } from '../components/board/BoardDetail';
import CardDetailModal from '../components/board/CardDetailModal';
import OutboxSidebar from '../components/board/OutboxSidebar';
import useAuth from '../hooks/useAuth';
import styled from "styled-components";

const PageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden; /* 사이드바 애니메이션을 위해 */
`;

const MainContentWrapper = styled.div<{ isOpen: boolean }>`
    width: 100%;
    height: 100%;
    transition: transform 0.3s ease-in-out;
    transform: translateX(${props => props.isOpen ? '350px' : '0'});
`;

const BoardPage = () => {
    useAuth();
    const { boardId } = useParams<{ boardId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [board, setBoard] = useState<ApiBoard | null>(null);
    const [lists, setLists] = useState<ListWithCards[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const isOutboxOpen = location.pathname.endsWith('/outbox');

    const fetchBoard = useCallback(async () => {
        if (boardId) {
            try {
                const response = await getBoardById(boardId);
                setBoard(response.data);
            } catch (error) {
                console.error("Failed to fetch board", error);
                navigate('/');
            }
        }
    }, [boardId, navigate]);

    useEffect(() => {
        fetchBoard();
    }, [fetchBoard]);

    const triggerRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    if (!board) {
        return <div>Loading board...</div>;
    }

    return (
        <PageContainer>
            <OutboxSidebar
                isOpen={isOutboxOpen}
                boardId={board.id}
                lists={lists}
                onClose={() => navigate(`/board/${board.id}`)}
                onViewCard={(cardId) => navigate(`/board/${board.id}/outbox/card/${cardId}`)}
                onToggleComplete={async (card) => {
                    await updateCard({ id: card.id, completed: !card.completed });
                    triggerRefresh();
                }}
                key={`outbox-${refreshKey}`}
            />
            <MainContentWrapper isOpen={isOutboxOpen}>
                <BoardDetail
                    key={refreshKey}
                    selectedBoard={board}
                    onBack={() => navigate('/')}
                    onBoardUpdate={setBoard}
                    onListsUpdate={setLists}
                    isOutboxOpen={isOutboxOpen}
                    onRefreshNeeded={triggerRefresh}
                />
            </MainContentWrapper>
            <Routes>
                <Route path="card/:cardId" element={
                    <CardRouteWrapper
                        onClose={() => navigate(`/board/${board.id}`)}
                        onUpdateNeeded={triggerRefresh}
                    />}
                />
                <Route path="outbox/card/:cardId" element={
                    <CardRouteWrapper
                        onClose={() => navigate(`/board/${board.id}/outbox`)} // Outbox로 돌아가기
                        onUpdateNeeded={triggerRefresh}
                    />}
                />
            </Routes>
        </PageContainer>
    );
};

// --- 라우팅을 위한 Helper 컴포넌트들 ---
const CardRouteWrapper: React.FC<{ onClose: () => void, onUpdateNeeded: () => void }> = ({ onClose, onUpdateNeeded }) => {
    const { cardId } = useParams<{ cardId: string }>();
    const [card, setCard] = useState<ApiCard | null>(null);
    const user = useAuth();
    const isReadOnly = card?.userId !== user?.id && card?.assigneeId !== user?.id; // 담당자는 진행을 고칠 수 있다

    useEffect(() => {
        const fetchCard = async () => {
            if (cardId) {
                try {
                    const response = await getCardById(cardId);
                    setCard(response.data);
                } catch (error) {
                    console.error("Failed to fetch card", error);
                    onClose();
                }
            }
        };
        fetchCard();
    }, [cardId, onClose]);

    if (!card) return null;

    const handleToggleComplete = async () => {
        const originalCard = { ...card };
        try {
            await updateCard({ id: card.id, completed: !card.completed });
            onUpdateNeeded();
            onClose();
        } catch (error: any) {
            console.error("Failed to toggle complete", error);
            setCard(originalCard);
            // 백엔드에서 보낸 에러 메시지 표시
            const message = error.response?.data?.data || "완료 처리 실패";
            alert(message);
        }
    };

    // --- THIS IS THE FIX ---
    // onDelete 핸들러 안에 deleteCard API 호출 로직을 추가합니다.
    const handleDelete = async () => {
        if(card) {
            await deleteCard(card.id);
            onUpdateNeeded();
            onClose();
        }
    };

    return (
        <CardDetailModal
            card={card}
            onClose={onClose}
            onUpdate={(updatedCard) => {
                setCard(updatedCard);
                onUpdateNeeded();
            }}
            onDelete={handleDelete}
            onToggleComplete={handleToggleComplete}
            fromBoard={true}
            readOnly={isReadOnly}
        />
    );
};

export default BoardPage;

